import { Link, useParams } from 'react-router-dom';
import { getServiceDefinitionBySlug } from 'wasp/client/operations';

export default function ServiceDefinitionDetailPage() {
  const { slug } = useParams();
  const { data: definition, isLoading, error } = getServiceDefinitionBySlug({ slug: slug! }) as any;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading service...</p>
        </div>
      </div>
    );
  }

  if (error || !definition) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 text-lg mb-4">Service not found</p>
          <Link to="/services" className="text-blue-600 hover:text-blue-800">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <Link to="/discover-services" className="text-blue-600 hover:text-blue-800">
            Services
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/discover-services/definitions" className="text-blue-600 hover:text-blue-800">
            Service Categories
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600 dark:text-gray-400">{definition.name}</span>
        </nav>

        {/* Main Content */}
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
              {definition.imageUrl && (
                <img
                  src={definition.imageUrl}
                  alt={definition.name}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}

              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  {definition.name}
                </h1>
                <div className="flex flex-wrap gap-2">
                  <span className="px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-medium">
                    {definition.serviceType.label}
                  </span>
                  {definition.isRecurring && (
                    <span className="px-4 py-2 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-medium">
                      Recurring
                    </span>
                  )}
                  {definition.requiresBooking && (
                    <span className="px-4 py-2 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 font-medium">
                      Booking Required
                    </span>
                  )}
                </div>
              </div>

              {definition.description && (
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                  {definition.description}
                </p>
              )}

              {definition.detailedDescription && (
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: definition.detailedDescription }}
                />
              )}

              {/* Service Info */}
              <div className="grid gap-4 sm:grid-cols-2 mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                {definition.typicalDuration && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Typical Duration</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {definition.typicalDuration} minutes
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Available Locations</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {definition.offerings?.length || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Related Topics */}
            {definition.topicServices && definition.topicServices.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Related Topics
                </h2>
                <div className="flex flex-wrap gap-3">
                  {definition.topicServices.map((ts: any) => (
                    <Link
                      key={ts.topic.id}
                      to={`/topics/${ts.topic.slug}` as any}
                      className="px-4 py-2 rounded-lg bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-800 transition"
                    >
                      {ts.topic.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Occasions */}
            {definition.occasionServices && definition.occasionServices.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Related Occasions
                </h2>
                <div className="flex flex-wrap gap-3">
                  {definition.occasionServices.map((os: any) => (
                    <Link
                      key={os.occasion.id}
                      to={`/occasions/${os.occasion.slug}` as any}
                      className="px-4 py-2 rounded-lg bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800 transition"
                    >
                      {os.occasion.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Offerings */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 sticky top-8">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Find This Service
              </h2>

              {definition.offerings && definition.offerings.length > 0 ? (
                <div className="space-y-4">
                  {definition.offerings.slice(0, 5).map((offering: any) => (
                    <Link
                      key={offering.id}
                      to={`/discover-services/offerings/${offering.id}` as any}
                      className="block p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition"
                    >
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {offering.location.name}
                      </h3>
                      {offering.organization && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {offering.organization.name}
                        </p>
                      )}
                      {offering.scheduleType && (
                        <span className="inline-block text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded capitalize">
                          {offering.scheduleType}
                        </span>
                      )}
                    </Link>
                  ))}

                  {definition.offerings.length > 5 && (
                    <Link
                      to="/discover-services/offerings"
                      className="block text-center py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View all {definition.offerings.length} locations →
                    </Link>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No locations currently offering this service
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Check back later or contact your local community center
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}



